/**
 * Subscription Checking Utilities
 * Validates user subscription status and feature access
 */

import type { SubscriptionPlan } from "./plans";
import { getPlanFeatures, canAccessFeature } from "./plans";
import type { AiProfile } from "@/types/ai";

/**
 * Get user's subscription plan from profile
 */
export function getUserPlan(profile: AiProfile): SubscriptionPlan {
  if (profile.plan === "enterprise") return "enterprise";
  if (profile.plan === "pro_plus") return "pro_plus";
  if (profile.plan === "pro") return "pro";
  return "free";
}

/**
 * Check if user has access to a specific feature
 */
export function checkFeatureAccess(
  profile: AiProfile,
  feature: keyof ReturnType<typeof getPlanFeatures>
): { allowed: boolean; plan: SubscriptionPlan; reason?: string } {
  const plan = getUserPlan(profile);
  const allowed = canAccessFeature(plan, feature);
  
  if (!allowed) {
    return {
      allowed: false,
      plan,
      reason: `Feature requires ${getRequiredPlan(feature)} or higher subscription`,
    };
  }
  
  return { allowed: true, plan };
}

/**
 * Get the minimum plan required for a feature
 */
function getRequiredPlan(feature: keyof ReturnType<typeof getPlanFeatures>): SubscriptionPlan {
  const features = {
    free: getPlanFeatures("free"),
    pro: getPlanFeatures("pro"),
    pro_plus: getPlanFeatures("pro_plus"),
    enterprise: getPlanFeatures("enterprise"),
  };

  if (features.enterprise[feature]) return "enterprise";
  if (features.pro_plus[feature]) return "pro_plus";
  if (features.pro[feature]) return "pro";
  return "free";
}

/**
 * Validate subscription and return error if invalid
 */
export function validateSubscription(
  profile: AiProfile,
  feature: keyof ReturnType<typeof getPlanFeatures>
): { valid: boolean; error?: string } {
  const access = checkFeatureAccess(profile, feature);
  
  if (!access.allowed) {
    return {
      valid: false,
      error: access.reason || "Subscription required",
    };
  }
  
  return { valid: true };
}

